import {TextInput, Toggle, Button} from '@/components/atoms'
import {DropdownWithTags, SectionTitle} from '@/components/molecules'
import {PageContainer} from '@/components/organisms'
import {normalizeSubCategories} from '@/helpers/shadcn/utils'
import {useMemo} from 'react'

// Helper function to transform category format if needed
const transformCategory = cat => {
  if (!cat) return null

  if (cat?.categoryId) {
    return {
      id: cat?.categoryId,
      displayText: cat?.categoryName,
      isBadge: false,
    }
  }
  return cat
}

const CategoryEdit = ({
  categoryFields,
  brandAndCategoryFormData,
  categoryListData,
  handleFormChange,
  errors,
  localFormData,
  languages,
  addEmptySubCategory,
  removeSubCategory,
  subCategoryErrors = {},
}) => {
  const isSubCatBtnHidden = localFormData?.subCategoryFlag && localFormData?.subCategories?.length >= 10
  const dropdownList = useMemo(
    () =>
      categoryListData?.length &&
      categoryListData?.map(item => ({
        displayText: item?.category,
        id: item?.categoryId,
        isBadge: false,
      })),
    [categoryListData]
  )

  const isSubCategoryVisible = localFormData?.subCategoryFlag
  const subCategoriesList = localFormData?.subCategories || []
  return (
    <PageContainer data-testid="product-edit-category-content">
      <div className="grid grid-cols-1">
        <SectionTitle label="Category Information" />
        <div className="grid grid-cols-4 gap-4 pb-5">
          <div className="col-4 pt-5">
            <TextInput
              id={`erpCategory`}
              className="text-sm"
              isDark={true}
              label={categoryFields?.erpCategory?.label}
              placeholderText={categoryFields?.erpCategory?.placeholder}
              value={localFormData?.erpCategory || ''}
              error={errors?.erpCategory} // Display error message if it exists
              disabled={categoryFields?.erpCategory?.isDisabled}
              onChange={value =>
                handleFormChange({category: {...localFormData, erpCategory: value}}, 'erpCategory', value)
              }
            />
          </div>
          <div className="col-4 pt-5">
            <TextInput
              id="erpCategoryId"
              className="text-sm"
              isDark={true}
              label={categoryFields?.erpCategoryId?.label}
              placeholderText={categoryFields?.erpCategoryId?.placeholder}
              value={localFormData?.erpCategoryId || ''}
              error={errors?.erpCategoryId} // Display error message if it exists
              disabled={categoryFields?.erpCategoryId?.isDisabled}
              onChange={value =>
                handleFormChange({category: {...localFormData, erpCategoryId: value}}, 'erpCategoryId', value)
              }
            />
          </div>

          <div className="col-4 pt-5">
            <TextInput
              className="text-sm"
              isDark={true}
              label={categoryFields?.categoryType?.label}
              placeholderText={categoryFields?.categoryType?.placeholder}
              value={localFormData?.categoryType || ''}
              error={errors?.categoryType} // Display error message if it exists
              disabled={categoryFields?.categoryType?.isDisabled}
              onChange={value =>
                handleFormChange({category: {...localFormData, categoryType: value}}, 'categoryType', value)
              }
            />
          </div>
        </div>
        <div className="sc-border grid gap-4 pb-5 pt-5">
          <DropdownWithTags
            label="Categories"
            placeholder="Categories"
            childList={dropdownList ?? []}
            selectedOptions={brandAndCategoryFormData?.category?.categories?.map(transformCategory)}
            helperText="Multiple categories can be chosen."
            error={errors?.categories} // Display error message if it exists
            onChange={value => handleFormChange({category: {...localFormData, categories: value}}, 'categories', value)}
          />
        </div>
        <div className="col-4 pt-5">
          <TextInput
            className="text-sm"
            isDark={true}
            required={true}
            label={categoryFields?.displayCategoryId?.label}
            placeholderText={categoryFields?.displayCategoryId?.placeholder}
            value={localFormData?.displayCategoryId || ''}
            error={errors?.displayCategoryId} // Display error message if it exists
            onChange={value =>
              handleFormChange({category: {...localFormData, displayCategoryId: value}}, 'displayCategoryId', value)
            }
          />
        </div>
        <div className="sc-border grid grid-cols-4 gap-4 pb-5">
          {languages?.map(lang => {
            const languageName = lang.language
            const language = lang.code

            return (
              <div key={language} className="col-4 pt-5">
                <h5 className="heading pb-3 font-bold">{languageName}</h5>
                <TextInput
                  className="pt-2 text-sm"
                  isDark={true}
                  label={categoryFields?.displayCategory?.label}
                  placeholderText={categoryFields?.displayCategory?.placeholder}
                  value={localFormData?.displayCategory?.[language] || ''}
                  error={errors?.displayCategory?.[language]}
                  required={true}
                  onChange={value =>
                    handleFormChange(
                      {
                        ...localFormData,
                        displayCategory: {
                          ...localFormData?.displayCategory,
                          [language]: value,
                        },
                      },
                      'displayCategory',
                      value,
                      language
                    )
                  }
                />
              </div>
            )
          })}
        </div>
        <div className="grid gap-4 pb-5 pt-5">
          <Toggle
            uiSchema={{size: 'medium', isDark: true, label: 'Sub Category'}}
            value={localFormData?.subCategoryFlag}
            error={errors?.subCategoryFlag} // Display error message if it exists
            updateValue={value => {
              handleFormChange(null, 'subCategoryFlag', value)
              if (value === false) {
                handleFormChange(null, 'subCategoryId', '')
                handleFormChange(
                  {
                    category: {
                      ...localFormData,
                      subCategories: null,
                    },
                  },
                  'subCategories',
                  null
                )
              }
            }}
          />
        </div>

        {isSubCategoryVisible && (
          <div className="grid gap-4 pb-5">
            {subCategoriesList?.length > 0 &&
              subCategoriesList?.map((sub, idx) => (
                <div key={sub?.id} className="">
                  <div className="max-w-375 pt-5">
                    <TextInput
                      className="text-sm"
                      isDark={true}
                      label={categoryFields?.subCategoryId?.label}
                      placeholderText={categoryFields?.subCategoryId?.placeholder}
                      value={sub?.code || ''}
                      maxLength={255}
                      required={true}
                      error={subCategoryErrors[idx] || errors?.subCategoryId} // Display duplicate error or other error
                      onChange={value =>
                        handleFormChange(
                          {category: {...localFormData, subCategoryId: value}},
                          'subCategoryId',
                          value,
                          {en: 'en'},
                          {idx: idx}
                        )
                      }
                    />
                  </div>
                  <h5 className="heading pb-3 font-bold">{sub?.name}</h5>

                  {/* ---- Languages inside each Subcategory ---- */}
                  <div className="col-4 grid grid-cols-4 items-end gap-4">
                    {languages?.map(lang => {
                      const languageName = lang?.language
                      const language = lang?.code
                      return (
                        <div key={sub?.id + '-' + language} className="pb-4 pt-2">
                          <h6 className="font-medium">{languageName}</h6>

                          <TextInput
                            className="pt-2 text-sm"
                            isDark={true}
                            maxLength={255}
                            label={categoryFields?.subCategory?.label}
                            placeholderText={categoryFields?.subCategory?.placeholder}
                            value={localFormData?.subCategories?.[idx]?.values?.[language] || ''}
                            error={errors?.subCategories?.[sub?.id]?.[language]}
                            onChange={value => handleFormChange(null, 'subCategories', value, language, {idx: idx})}
                            required={true}
                          />
                        </div>
                      )
                    })}
                    {idx !== 0 && subCategoriesList?.length - 1 === idx ? (
                      <Button
                        handleButton={() => removeSubCategory(idx)}
                        uiSchema={{
                          title: 'Delete Sub Category',
                          variant: 'tertiary',
                          size: 'medium',
                          className: 'mb-3',
                          isDark: true,
                          iconLeading: '',
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
          </div>
        )}

        {isSubCategoryVisible && (
          <Button
            handleButton={() => addEmptySubCategory()}
            uiSchema={{
              title: 'Add Sub Category',
              variant: 'tertiary',
              size: 'medium',
              className: `${isSubCatBtnHidden && 'opacity-25'}`,
              isDark: true,
              iconLeading: 'add_box',
            }}
          />
        )}
      </div>
    </PageContainer>
  )
}

export default CategoryEdit
